"use client";

import { useActionState } from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { AlertCircle } from "lucide-react";
import { signup, type AuthResult } from "@/actions/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GlowCard } from "@/components/ui/glow-card";
import { Separator } from "@/components/ui/separator";
import { SSOButtons } from "@/components/auth/sso-buttons";
import { springPresets } from "@/lib/motion";

const initialState: AuthResult = {};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } },
};

const staggerItem = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
};

export function SignupForm() {
  const [state, formAction, isPending] = useActionState(signup, initialState);

  return (
    <GlowCard interactive glow="hover" className="w-full max-w-md">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-heading-2 text-foreground">Create your account</h2>
          <p className="mt-1 text-body text-muted-foreground">
            Set up your team and start reaching sellers.
          </p>
        </div>

        <SSOButtons mode="signup" />

        <form action={formAction}>
          <motion.div
            className="space-y-4"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            {state.error && (
              <motion.div
                className="glass-1 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-destructive"
                variants={staggerItem}
                transition={springPresets.gentle}
              >
                <AlertCircle className="size-4 shrink-0" />
                {state.error}
              </motion.div>
            )}

            <motion.div className="space-y-2" variants={staggerItem} transition={springPresets.gentle}>
              <Label htmlFor="fullName">Full name</Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                placeholder="Jane Smith"
                required
              />
            </motion.div>

            <motion.div className="space-y-2" variants={staggerItem} transition={springPresets.gentle}>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="jane@example.com"
                required
              />
            </motion.div>

            <motion.div className="space-y-2" variants={staggerItem} transition={springPresets.gentle}>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
            </motion.div>

            <motion.div className="space-y-2" variants={staggerItem} transition={springPresets.gentle}>
              <Label htmlFor="teamName">Team name</Label>
              <Input
                id="teamName"
                name="teamName"
                type="text"
                placeholder="Smith Realty Group"
                required
              />
            </motion.div>
          </motion.div>

          <motion.div
            className="mt-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.3 }}
          >
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={isPending}
            >
              {isPending ? "Creating account..." : "Create account"}
            </Button>
          </motion.div>
        </form>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.35 }}
        >
          <Separator className="my-5" />
          <div className="text-center">
            <p className="mb-3 text-caption text-muted-foreground">
              Already have an account?
            </p>
            <Button variant="outline" size="sm" asChild className="w-full">
              <Link href="/login">Log in</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </GlowCard>
  );
}
